import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationInvitesService } from '../services/organization-invites.service'; // adjust path as needed
import { InMemoryCacheService, User, UtilsService } from 'src/app/common';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { OrganizationSettingsService } from '../services/organization-settings.service';

@Component({
  selector: 'gig-accept-invite',
  standalone: true,
  imports: [CommonModule, ButtonModule, RippleModule],
  templateUrl: './accept-invite.component.html',
  styleUrl: './accept-invite.component.scss'
})
export class AcceptInviteComponent implements OnInit {

    private route = inject(ActivatedRoute);
    public router = inject(Router);
    private orgInvites = inject(OrganizationInvitesService);
    private utils = inject(UtilsService);
    public cache = inject(InMemoryCacheService);

    public token: string | null = null;
    public acceptResult: any = null;
    public error: string | null = null;
    public errorStatus: number | null = null;
    public isLoading: boolean = false;
    public showLogOrRegButton: boolean = false;
    public attemptedPostRegister: boolean = false;
    private organizationSettingsService = inject(OrganizationSettingsService);

    async ngOnInit() {
        this.utils.logger.info('AcceptInviteComponent initialized');
        this.token = this.route.snapshot.queryParamMap.get('token');
        if (this.token) {
            await this.tryAcceptInvite();
        }
    }

    async tryAcceptInvite() {
        this.utils.logger.info('AcceptInviteComponent: Trying to accept invite');
        const user: User = this.cache.user;
        this.isLoading = true;
        this.error = null;
        this.showLogOrRegButton = false;
        this.acceptResult = null;

        const fromLogin = this.route.snapshot.queryParamMap.get('fromLogin') || 0;
        const fromRegister = this.route.snapshot.queryParamMap.get('fromRegister') || 0;

        try {

            if(user && (fromLogin === '1' || fromRegister === '1')) {
                this.acceptResult = await this.resolveAcceptedInviteMembership(String(user.id), this.token!);

                if(fromRegister === '1') {
                    this.utils.logger.info('AcceptInviteComponent: fromRegister membership accepted', this.acceptResult);

                    this.attemptedPostRegister = true;
                    this.cache.logout(false); // Don't navigate to login, we will handle navigation below
                    this.utils.logger.info('AcceptInviteComponent: Detected fromRegister, logging out user');
                }
                else if(fromLogin === '1') {
                    this.utils.logger.info('AcceptInviteComponent: fromLogin membership accepted', this.acceptResult);


                    //this.cache.logout(false); // Don't navigate to login, we will handle navigation below
                    this.utils.logger.info('AcceptInviteComponent: Detected fromLogin, clearing cache');
                }
                else {
                    this.utils.logger.info('AcceptInviteComponent: Invite membership resolved', this.acceptResult);
                }
            }
            else{
                // this.errorStatus = 401;
                this.error = 'You must either log in if you have an account or register to create a new one.';
                this.showLogOrRegButton = true;
            }
        } catch (err: any) {
            this.utils.logger.error('AcceptInviteComponent: Error accepting invite', err);

            // If 403 Forbidden - Logged in but wrong user
            if (err?.status === 403) {
                this.showLogOrRegButton = true;
            }
            else if (err?.status === 401) {
                this.showLogOrRegButton = true;
            }

            // Backend provided title message
            this.error = err?.error?.title ?? err?.message ?? 'Failed to accept invitation.';
            this.errorStatus = err?.status ?? 500;
        } finally {
            this.isLoading = false;
        }
    }

    private async resolveAcceptedInviteMembership(userId: string, inviteToken: string): Promise<any> {
        this.utils.logger.info('AcceptInviteComponent: resolving org + membership from invite token');

        const orgFromInvite = await this.orgInvites.getOrganizationByInviteToken(inviteToken);

        this.utils.logger.info('Organizational ID: ', orgFromInvite);

        if (!orgFromInvite) {
            throw new Error('Unable to resolve organization from invite token.');
        }

        const orgId = Number(orgFromInvite?.organizationId);
        if (!orgId) {
            throw new Error('Invite organization payload missing organization id.');
        }

        const membership = await this.organizationSettingsService.getOrgMembershipByOrganizationAndUserId(orgId, userId);

        if (!membership) {
            throw new Error('No organization membership found for invite organization.');
        }

        if (String(membership?.status ?? '').toLowerCase() !== 'active') {
            throw new Error(`Membership status is "${membership?.status ?? 'Unknown'}".`);
        }

        return {
            ...membership,
            organizationId: orgId,
            organizationName: orgFromInvite?.name ?? orgFromInvite?.organizationName ?? membership?.organizationName
        };
    }

    goToLoginWithToken() {
        this.cache.logout(false); // Don't navigate to login, we will handle navigation below
        // Navigation with state, so we can know we came from this page
        this.router.navigate(['account/login'], {
            queryParams: { inviteToken: this.token },
        });
    }

    goToLogin() {
        this.cache.logout();
    }

    public async switchOrgs() {
        // attempt to call set org context to switch them to the new org, if they were already logged in
        this.utils.logger.info('AcceptInviteComponent: Detected fromLogin, attempting to set org context');
        const orgContext = await this.organizationSettingsService.setOrganizationContext(this.acceptResult.organizationId);
        if (orgContext) {
            this.utils.logger.info('AcceptInviteComponent: Successfully set org context, navigating to dashboard');
            this.cache.organization = orgContext.organization; // Update cache with new org context
            this.router.navigate(['/dashboard']);
        } else {
            this.utils.logger.error('AcceptInviteComponent: Failed to set org context after accepting invite');
            this.error = 'Invite accepted but failed to set organization context. Please try logging in again.';
            this.showLogOrRegButton = true;
        }
    }

    stayLoggedIn() {
        // Dashboard should detect the new org and switch to it, but we can also force a reload to ensure all data is refreshed for the new org context
        this.router.navigate(['/dashboard']).then(() => {
            window.location.reload();
        });
    }

    goToRegister() {
        this.cache.logout(false); // Don't navigate to login, we will handle navigation below
        // Navigation with state, so we can know we came from this page
        this.router.navigate(['account/register'], {
            queryParams: { inviteToken: this.token },
        });
    }

    getStatusTitle(statusCode: number): string {
        switch (statusCode) {
            case 400:
                return 'Bad Request';
            case 401:
                return 'Unauthorized';
            case 403:
                return 'Forbidden';
            case 404:
                return 'Not Found';
            case 408:
                return 'Request Timeout';
            case 409:
                return 'Conflict';
            case 422:
                return 'Validation Error';
            case 429:
                return 'Too Many Requests';
            case 500:
                return 'Internal Server Error';
            case 502:
                return 'Bad Gateway';
            case 503:
                return 'Service Unavailable';
            case 504:
                return 'Gateway Timeout';
            default:
                return 'Unexpected Error';
        }
    }

}
